import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useThemeStore } from './stores/themeStore'
import GlobalErrorBoundary from './components/GlobalErrorBoundary'
import { ConfirmProvider } from './components/ConfirmProvider'
import App from './App'
import './index.css'
// i18n initialization is handled in App.jsx to avoid double initialization

// Service worker registration is handled automatically by vite-plugin-pwa

// Initialize Sentry for frontend error tracking (optional)
// Only loads if VITE_SENTRY_DSN is configured AND @sentry/react package is installed
// 
// To enable Sentry: 
//   1. Install package: npm install @sentry/react
//   2. Set VITE_SENTRY_DSN in .env file
//
// Note: Sentry is optional - only initialize if DSN is configured AND package is installed
// Use a more robust dynamic import that won't fail at build time
if (import.meta.env.VITE_SENTRY_DSN) {
  // Use setTimeout to defer import until runtime, avoiding Vite static analysis
  setTimeout(() => {
    // Use Function constructor to create a dynamic import that Vite won't analyze
    const dynamicImport = new Function('specifier', 'return import(specifier)')
    dynamicImport('@sentry/react')
      .then((SentryModule) => {
        const Sentry = SentryModule.default || SentryModule
        
        if (Sentry && typeof Sentry.init === 'function') {
          Sentry.init({
            dsn: import.meta.env.VITE_SENTRY_DSN,
            environment: import.meta.env.MODE || 'development',
            integrations: [
              new Sentry.BrowserTracing(),
              new Sentry.Replay(),
            ],
            tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
          })
          console.info('[Sentry] Error tracking initialized')
        }
      })
      .catch((error) => {
        // Sentry package not installed - this is fine for development
        if (import.meta.env.MODE === 'development') {
          console.info('[Sentry] Package not installed (optional). To enable: npm install @sentry/react')
        } else {
          console.warn('[Sentry] Failed to initialize:', error.message)
        }
      })
  }, 0)
}

// Initialize theme on app start
useThemeStore.getState().initTheme()

// Suppress "Uncaught (in promise)" for Axios timeouts - we handle these with .catch() fallbacks
window.addEventListener('unhandledrejection', (event) => {
  const err = event?.reason
  if (err?.name === 'AxiosError' && (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout'))) {
    event.preventDefault()
    event.stopPropagation()
  }
})

const queryClient = new QueryClient({
  // react-query 5 kent geen onError in defaultOptions.queries meer (#454): globale
  // query-fouten lopen via de QueryCache.
  queryCache: new QueryCache({
    onError: (error) => {
      console.error('Query error:', error)
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // 503 = DNA_NOT_CONFIGURED → permanente fout, retry zinloos
      retry: (failureCount, error) => {
        const status = error?.response?.status ?? error?.status
        if (status === 503) return false
        return failureCount < 1
      },
      staleTime: 60 * 1000,      // 1 min: reduce unnecessary refetches
      gcTime: 5 * 60 * 1000,    // 5 min cache (was cacheTime in v4)
    },
    mutations: {
      onError: (error) => {
        console.error('Mutation error:', error)
      }
    }
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>,
)
